import { formatPriceNumberWithSymbol } from '@/utils/price';

interface CurrencyLike {
  symbol_prefix?: string;
  symbol_suffix?: string;
}

interface PublicHalfPriceRule {
  rule_flag?: number;
  discount_rate?: string | number;
  threshold_price?: string | number;
  restrict_single?: string | number;
}

export interface PublicHalfPriceConfig {
  content?: string;
  rule?: PublicHalfPriceRule;
}

interface TFunction {
  (key: string, options?: any): string;
}

export type CouponSectionLabel =
  | { type: 'coupon'; text: string }
  | { type: 'secondHalfPrice'; text: string }
  | { type: 'point' };

export const getSecondHalfPriceDiscountRate = (publicHalfPrice?: PublicHalfPriceConfig) =>
  String(publicHalfPrice?.rule?.discount_rate || '50');

export const getSecondHalfPriceLabelText = (publicHalfPrice: PublicHalfPriceConfig, t: TFunction) =>
  t('secondHalfPriceLabel', {
    percent: getSecondHalfPriceDiscountRate(publicHalfPrice),
  });

export const formatSecondHalfPriceContent = (
  publicHalfPrice?: PublicHalfPriceConfig,
  currency?: CurrencyLike,
) => {
  if (publicHalfPrice?.rule?.rule_flag !== 1) {
    return publicHalfPrice?.content || '';
  }

  let content = publicHalfPrice.content || '';
  const { discount_rate, threshold_price, restrict_single } = publicHalfPrice.rule;

  if (content.includes('{discount_rate}') && discount_rate) {
    content = content.replace('{discount_rate}', String(discount_rate));
  }

  if (content.includes('{threshold_price}') && threshold_price) {
    content = content.replace(
      '{threshold_price}',
      formatPriceNumberWithSymbol(String(threshold_price), currency),
    );
  }

  if (content.includes('{restrict_single}') && restrict_single) {
    content = content.replace('{restrict_single}', String(restrict_single));
  }

  return content;
};

export const buildCouponSectionLabels = ({
  couponTips,
  points,
  showSecondHalfPrice,
  publicHalfPrice,
  t,
}: {
  couponTips?: string;
  points: number;
  showSecondHalfPrice: boolean;
  publicHalfPrice?: PublicHalfPriceConfig;
  t: TFunction;
}) => {
  const labels: CouponSectionLabel[] = [];

  if (couponTips) {
    labels.push({ type: 'coupon', text: couponTips });
  }

  if (showSecondHalfPrice) {
    labels.push({
      type: 'secondHalfPrice',
      text: getSecondHalfPriceLabelText(publicHalfPrice, t),
    });
  }

  if (points >= 1) {
    labels.push({ type: 'point' });
  }

  return labels.slice(0, 2);
};

export const shouldHideCouponsText = (labels: CouponSectionLabel[]) =>
  labels.length === 2 && labels[0].type === 'coupon' && labels[1].type === 'secondHalfPrice';
